// Grafici interattivi dei report dati: Chart.js caricato solo su queste pagine.
// Ogni canvas dichiara i propri dati in data-report-chart; i numeri restano
// leggibili nelle tabelle dell'articolo anche senza JavaScript.
import Chart from 'chart.js/auto';

const INK = '#262e28';
const MUTED = '#5f6b62';
const GRID = '#e3e8dd';
const GREEN = '#1a7d47';
const RED = '#c1441f';
const GRAY = '#aab4ac';
const SURFACE = '#f9fbf4';

Chart.defaults.font.family = 'system-ui, -apple-system, "Segoe UI", sans-serif';
Chart.defaults.font.size = 13;
Chart.defaults.color = MUTED;
Chart.defaults.locale = 'it-IT';

const tooltip = {
  backgroundColor: '#1d2620',
  titleColor: '#f4f7ef',
  titleFont: { weight: 700 },
  bodyColor: '#dbe3d8',
  padding: 10,
  cornerRadius: 6,
  displayColors: false
};

function divergingBar(spec) {
  return {
    type: 'bar',
    data: {
      labels: spec.labels,
      datasets: [{
        data: spec.values,
        backgroundColor: spec.values.map((value) => (value >= 0 ? GREEN : RED)),
        borderRadius: 4,
        maxBarThickness: 22
      }]
    },
    options: {
      indexAxis: 'y',
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { color: GRID },
          ticks: {
            stepSize: 0.1,
            callback: (value) => {
              const rounded = Number(value.toFixed(2));
              return rounded > 0 ? `+${rounded}` : `${rounded}`;
            }
          }
        },
        y: {
          grid: { display: false },
          ticks: { color: INK, font: { size: 14, weight: 700 } }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltip,
          callbacks: {
            label: (context) => {
              const value = context.parsed.x;
              const signed = `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
              return `Scostamento medio ${signed} · ${spec.extra[context.dataIndex]}`;
            }
          }
        }
      }
    }
  };
}

function slope(spec) {
  const contextLine = (player) => ({
    label: player.label,
    data: player.values,
    borderColor: GRAY,
    backgroundColor: GRAY,
    borderWidth: 2,
    pointRadius: 3,
    pointHoverRadius: 6,
    pointBorderColor: SURFACE,
    pointBorderWidth: 2
  });

  return {
    type: 'line',
    data: {
      labels: spec.seasons,
      datasets: [
        ...spec.context.map(contextLine),
        {
          label: spec.highlight.label,
          data: spec.highlight.values,
          borderColor: GREEN,
          backgroundColor: GREEN,
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBorderColor: SURFACE,
          pointBorderWidth: 2
        }
      ]
    },
    options: {
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false },
      scales: {
        x: { grid: { display: false }, offset: true },
        y: { grid: { color: GRID }, ticks: { callback: (value) => value.toFixed(1) } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltip,
          callbacks: {
            title: (items) => items[0]?.dataset.label,
            label: (context) => `${context.label}: fantamedia ${context.parsed.y.toFixed(2)}`
          }
        }
      }
    }
  };
}

function dumbbell(spec) {
  return {
    type: 'bar',
    data: {
      labels: spec.rows.map((row) => row.label),
      datasets: [{
        data: spec.rows.map((row) => [Math.min(row.from, row.to), Math.max(row.from, row.to)]),
        backgroundColor: spec.rows.map((row) => (row.to >= row.from ? GREEN : RED)),
        borderRadius: 999,
        borderSkipped: false,
        barThickness: 10
      }]
    },
    options: {
      indexAxis: 'y',
      maintainAspectRatio: false,
      scales: {
        x: { beginAtZero: true, grid: { color: GRID } },
        y: { grid: { display: false }, ticks: { color: INK, font: { size: 14, weight: 700 } } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltip,
          callbacks: {
            label: (context) => {
              const row = spec.rows[context.dataIndex];
              return `${spec.fromLabel}: ${row.from} → ${spec.toLabel}: ${row.to} (${row.delta})`;
            }
          }
        }
      }
    }
  };
}

function line(spec) {
  return {
    type: 'line',
    data: {
      labels: spec.labels,
      datasets: [{
        data: spec.values,
        borderColor: GREEN,
        backgroundColor: 'rgba(26, 125, 71, 0.08)',
        fill: true,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: GREEN,
        pointBorderColor: SURFACE,
        pointBorderWidth: 2,
        tension: 0.15
      }]
    },
    options: {
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { grid: { display: false } },
        y: { grid: { color: GRID } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltip,
          callbacks: {
            label: (context) => `${context.parsed.y} ${spec.unit || ''}`.trim()
          }
        }
      }
    }
  };
}

function scatter(spec) {
  const series = (points, label, color) => ({
    label,
    data: points,
    backgroundColor: color,
    pointRadius: 4,
    pointHoverRadius: 7,
    pointBorderColor: SURFACE,
    pointBorderWidth: 1
  });

  return {
    type: 'scatter',
    data: {
      datasets: [
        series(spec.points.filter((p) => p.y >= 0), spec.upLabel, GREEN),
        series(spec.points.filter((p) => p.y < 0), spec.downLabel, RED)
      ]
    },
    options: {
      maintainAspectRatio: false,
      scales: {
        x: { title: { display: true, text: spec.xLabel }, grid: { color: GRID } },
        y: { title: { display: true, text: spec.yLabel }, grid: { color: GRID } }
      },
      plugins: {
        legend: { display: true, labels: { usePointStyle: true, boxHeight: 7 } },
        tooltip: {
          ...tooltip,
          callbacks: {
            label: (context) => {
              const raw = context.raw;
              const signed = `${raw.y > 0 ? '+' : ''}${raw.y.toFixed(2)}`;
              return `${raw.label}: ${raw.x} fantamilioni, scostamento ${signed}`;
            }
          }
        }
      }
    }
  };
}

const BUILDERS = { 'diverging-bar': divergingBar, slope, dumbbell, line, scatter };

// Selettore lega 8/10/12: aggiorna i prezzi massimi consigliati nelle tabelle.
document.querySelectorAll('.report-league-switch__btn').forEach((button) => {
  button.addEventListener('click', () => {
    const league = button.dataset.league;
    document.querySelectorAll('.report-league-switch__btn').forEach((item) => {
      item.classList.toggle('is-active', item === button);
    });
    document.querySelectorAll('.max-price').forEach((cell) => {
      cell.textContent = cell.dataset[`l${league}`];
    });
  });
});

document.querySelectorAll('canvas[data-report-chart]').forEach((canvas) => {
  const spec = JSON.parse(canvas.dataset.reportChart);
  const builder = BUILDERS[spec.kind];
  if (builder) {
    new Chart(canvas, builder(spec));
  }
});
